import React from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import {
  ArrowRight,
  CheckCircle2,
  Landmark,
  Factory,
  School,
  Building2,
  ShieldCheck,
  Activity,
  Globe2,
  Droplets,
} from "lucide-react";

import { assets } from "../data/siteData";

/* ---------------- CLIENT LOGOS ---------------- */
const clientLogos = [
  { name: "Client 1", image: assets.jsw },
  { name: "Client 2", image: assets.coal },
  { name: "Client 3", image: assets.iocl },
  { name: "Client 4", image: assets.iiest },
  { name: "Client 5", image: assets.tata },
  { name: "Client 6", image: assets.goi },
  { name: "Client 7", image: assets.grse },
  {name: "Client 8", image: assets.jbj },
];

/* ---------------- DATA ---------------- */
const sectors = [
  {
    title: "Municipal & Government",
    text: "Engineered water access, treatment, and dispensing systems for public infrastructure and community-scale deployment.",
    icon: Landmark,
  },
  {
    title: "Industrial & Commercial",
    text: "Process-ready water systems for industrial sites, hospitality, manufacturing environments, and commercial assets.",
    icon: Factory,
  },
  {
    title: "Institutions & Campuses",
    text: "Reliable purification and monitoring infrastructure for schools, colleges, hospitals, and institutional operations.",
    icon: School,
  },
  {
    title: "Development & CSR Programs",
    text: "Safe-water systems aligned with impact, resilience, and long-term community service delivery goals.",
    icon: Globe2,
  },
];

const trustBlocks = [
  {
    title: "Built for operating reality",
    text: "Hydrotec systems are shaped around site conditions, user patterns, infrastructure constraints, and long-term serviceability.",
    icon: ShieldCheck,
  },
  {
    title: "Connected visibility",
    text: "Our solutions can integrate monitoring, alerts, and reporting for stronger operational awareness and accountability.",
    icon: Activity,
  },
  {
    title: "Execution with continuity",
    text: "From deployment through support, we focus on systems that work reliably in the field and remain manageable over time.",
    icon: CheckCircle2,
  },
];

const stats = [
  { value: "450+", label: "Installations & impact points" },
  { value: "24/7", label: "Monitoring-oriented systems" },
  { value: "Multi-sector", label: "Client environments served" },
];

/* ---------------- COMPONENT ---------------- */
export default function ClientsPage() {
  return (
    <div className="pt-[104px]">
      <SEO
        title="Our Clients | Water Treatment Projects Across Kolkata, West Bengal | Hydrotec"
        description="Hydrotec Solutions has served 450+ clients including JSW, IOCL, GRSE, IIEST, and institutions across Kolkata, Newtown, Rajarhat, and West Bengal with water treatment, pump systems, and monitoring solutions."
        keywords="Hydrotec clients Kolkata, water treatment clients West Bengal, JSW water treatment, IOCL water system, water infrastructure clients Kolkata"
        canonical="https://hydroteclimited.com/clients"
      />
      <main className="bg-white text-[#122636]">

        {/* HERO */}
        <section className="relative min-h-[88vh] overflow-hidden bg-[#0c2132] text-white">
          <div className="absolute inset-0">
            <img
              src={assets.client}
              alt="Hydrotec client infrastructure"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(8,22,35,0.88)_0%,rgba(8,22,35,0.72)_34%,rgba(8,22,35,0.48)_58%,rgba(8,22,35,0.66)_100%)]" />
          </div>

          <div className="relative mx-auto flex min-h-[88vh] max-w-[1480px] items-end px-6 pb-16 pt-32">
            <div className="max-w-[860px]">
              <p className="text-sm font-semibold uppercase tracking-[0.32em] text-[#8bc84f]">
                Our Clients & Associates
              </p>

              <h1 className="mt-5 text-5xl font-semibold leading-[0.98] tracking-[-0.05em]">
                Trusted by organizations building modern water infrastructure.
              </h1>

              <p className="mt-7 max-w-[720px] text-lg text-white/78">
                Hydrotec supports public, industrial, institutional, and impact-led
                clients with engineered water systems.
              </p>
            </div>
          </div>
        </section>

        {/* 🚀 ROLLING LOGO STRIP */}
        <section className="border-b border-[#e8eef2] bg-[#f6f9fb] overflow-hidden">
          <div className="mx-auto max-w-[1480px] px-6 py-10">
            <p className="text-center text-xs font-semibold uppercase tracking-[0.3em] text-[#6f7f89]">
              Trusted by clients across sectors
            </p>

            <div className="relative mt-8 overflow-hidden">
              {/* fade edges */}
              <div className="absolute left-0 top-0 z-10 h-full w-20 bg-gradient-to-r from-[#f6f9fb] to-transparent" />
              <div className="absolute right-0 top-0 z-10 h-full w-20 bg-gradient-to-l from-[#f6f9fb] to-transparent" />

              {/* scrolling logos */}
              <div className="flex w-max animate-[clientMarquee_28s_linear_infinite] gap-6">
                {[...clientLogos, ...clientLogos].map((logo, index) => (
                  <div
                    key={index}
                    className="flex h-[190px] min-w-[160px] items-center justify-center rounded-xl border border-[#dde7ed] bg-white px-6 shadow-sm"
                  >
                    <img
                      src={logo.image}
                      alt={logo.name}
                      className="h-[148px] w-auto object-contain transition duration-300 hover:scale-105"
                    />
                  </div>))}
              </div>
            </div>
          </div>
        </section>

        
        {/* SECTORS */}
        <section className="bg-white">
          <div className="mx-auto max-w-[1480px] px-6 py-10">
            <h2 className="text-4xl font-semibold">
              Solutions aligned to diverse operating contexts.
            </h2>

            <div className="mt-12 grid gap-5 md:grid-cols-2 xl:grid-cols-4 color-[#5d6c77]">
              {sectors.map((item) => {
                const Icon = item.icon;
                return (
                  <div
  key={item.title}
  className="rounded-xl border border-green-200 bg-green-50 p-7 transition hover:shadow-lg"
>
  <Icon className="h-6 w-6 text-green-600" />

  <h3 className="mt-5 text-xl font-semibold text-green-900">
    {item.title}
  </h3>

  <p className="mt-3 text-sm text-green-700">
    {item.text}
  </p>
</div>               );
              })}
            </div>
          </div>
        </section>

      </main>
    </div>
  );
}